/**
 * Photo Memories — co-located typed client for the admin photo-scan routes
 * (backend `photoScan.js` → mounted at `/api/admin/quill/photo-scan`).
 *
 * Like operations/api.ts and agents/api.ts this REPLICATES the shared
 * `request`/`unwrap` seam from services/quillAdminService.ts (those helpers are
 * module-private; only `QuillApiError` is exported) rather than editing the
 * service.
 *
 * FLAG-TOLERANT: the whole feature is gated by FEATURE_PHOTO_MEMORIES and its
 * schema (538–541) is applied by Grant. Until then the reads come back
 * `{ success:true, degraded:true, code:'PHOTO_MEMORIES_NOT_ENABLED' }` and the
 * writes return 503 with that code — the page renders an honest "not enabled"
 * state rather than an error. The `degraded`/`code` fields survive `unwrap`
 * because the reads return them at the envelope top level; the client surfaces
 * them via the *WithMeta helpers below.
 *
 * Numeric aggregates (counters, cost) may arrive from node-pg as strings — every
 * consumer coerces with a Number() guard before arithmetic or formatting.
 */

import { QuillApiError } from '../../../services/quillAdminService';

const BASE = '/api/admin/quill';

interface EnvelopeShape {
  success?: boolean;
  code?: string;
  message?: string;
  error?: string;
  [key: string]: unknown;
}

function qs(params?: Record<string, string | number | boolean | undefined | null>): string {
  if (!params) return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init.headers || {}) },
    });
  } catch (e) {
    throw new QuillApiError(
      e instanceof Error ? e.message : 'Network error contacting Quill backend',
      0,
      'NETWORK_ERROR'
    );
  }

  let body: EnvelopeShape | null = null;
  try {
    body = await res.json();
  } catch {
    // No/invalid JSON body — handled below.
  }

  if (!res.ok || (body && body.success === false)) {
    const message = body?.message || body?.error || `Quill backend request failed (${res.status})`;
    throw new QuillApiError(message, res.status, body?.code);
  }
  return (body ?? {}) as T;
}

// ════════════════════════════════════════════════════════════════════════
// Types
// ════════════════════════════════════════════════════════════════════════

export { QuillApiError };

export type CountLike = number | string | null | undefined;

/** CHECK-constrained status of a `quill_photo_scan_runs` row (migration 538). */
export type PhotoScanStatus =
  | 'pending'
  | 'uploading'
  | 'detecting_faces'
  | 'clustering'
  | 'naming_wait'
  | 'activity'
  | 'synthesis'
  | 'verifying'
  | 'writing_memory'
  | 'complete'
  | 'halted'
  | 'failed';

/** The single admin config row (quill_photo_scan_config, 538). Null when the
 *  feature is not enabled yet (table absent). */
export interface PhotoScanConfig {
  scan_limit: number;
  top_k: number;
  min_cluster_size: number;
  updated_by: string | null;
  updated_reason: string | null;
  updated_at: string | null;
}

/** Body for PUT /photo-scan/config. updated_reason is REQUIRED by the backend. */
export interface UpdatePhotoScanConfigRequest {
  scan_limit: number;
  top_k: number;
  min_cluster_size: number;
  updated_reason: string;
}

/** One run row from GET /photo-scan/runs — every counter backs a screen claim. */
export interface PhotoScanRun {
  id: string;
  member_id: string | null;
  status: PhotoScanStatus | string;
  consent_id: string | null;
  scan_limit: CountLike;
  top_k: CountLike;
  photos_total: CountLike;
  gated_out_count: CountLike;
  uploaded_count: CountLike;
  faces_found: CountLike;
  clusters_count: CountLike;
  named_count: CountLike;
  skipped_count: CountLike;
  facts_written: CountLike;
  facts_rejected: CountLike;
  mentions_written: CountLike;
  captions_written: CountLike;
  phase_checkpoints: Record<string, unknown> | null;
  halt_requested: boolean;
  error_code: string | null;
  error_detail: string | null;
  created_at: string | null;
  updated_at: string | null;
  completed_at: string | null;
  rejected_candidates_count: CountLike;
  /** SUM(est_cost_usd) over cost_center 'photo-pipeline' in the run window.
   *  null when the usage ledger is unavailable (cost_degraded). */
  cost_actual_usd: CountLike;
  cost_calls: CountLike;
}

/** GET /photo-scan/runs envelope, incl. the flag-tolerant meta fields. */
export interface PhotoScanRunsResult {
  runs: PhotoScanRun[];
  /** True when the whole feature is off (schema not applied) — runs is empty. */
  notEnabled: boolean;
  /** True when the usage ledger is unavailable — costs read as unknown, not $0. */
  costDegraded: boolean;
}

/** GET /photo-scan/config result incl. the not-enabled discriminant. */
export interface PhotoScanConfigResult {
  config: PhotoScanConfig | null;
  notEnabled: boolean;
}

export interface HaltRunResult {
  id: string;
  status: PhotoScanStatus | string;
  halt_requested: boolean;
}

const NOT_ENABLED_CODE = 'PHOTO_MEMORIES_NOT_ENABLED';

// ════════════════════════════════════════════════════════════════════════
// Client
// ════════════════════════════════════════════════════════════════════════

export const photoScanApi = {
  /** GET /photo-scan/config. `{success, data, degraded?, code?}` — the reads
   *  never throw for "not enabled"; they carry a degraded flag we translate. */
  async getConfig(): Promise<PhotoScanConfigResult> {
    const body = await request<{ success: true; data: PhotoScanConfig | null; degraded?: boolean; code?: string }>(
      '/photo-scan/config'
    );
    return {
      config: body.data ?? null,
      notEnabled: Boolean(body.degraded) && body.code === NOT_ENABLED_CODE,
    };
  },

  /** PUT /photo-scan/config. Throws QuillApiError on 400 (validation),
   *  404 CONFIG_ROW_MISSING, or 503 PHOTO_MEMORIES_NOT_ENABLED. */
  async updateConfig(payload: UpdatePhotoScanConfigRequest): Promise<PhotoScanConfig> {
    const body = await request<{ success: true; data: PhotoScanConfig }>('/photo-scan/config', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return body.data;
  },

  /** GET /photo-scan/runs?limit=. Carries `degraded` (feature off) and
   *  `cost_degraded` (usage ledger off) meta alongside the run rows. */
  async getRuns(limit?: number): Promise<PhotoScanRunsResult> {
    const body = await request<{
      success: true;
      data: PhotoScanRun[];
      degraded?: boolean;
      code?: string;
      cost_degraded?: boolean;
    }>(`/photo-scan/runs${qs({ limit })}`);
    return {
      runs: body.data ?? [],
      notEnabled: Boolean(body.degraded) && body.code === NOT_ENABLED_CODE,
      costDegraded: Boolean(body.cost_degraded),
    };
  },

  /** POST /photo-scan/runs/:id/halt. Throws QuillApiError on 404 RUN_NOT_FOUND,
   *  409 RUN_ALREADY_TERMINAL, or 503 PHOTO_MEMORIES_NOT_ENABLED. */
  async haltRun(runId: string): Promise<HaltRunResult> {
    const body = await request<{ success: true; data: HaltRunResult }>(
      `/photo-scan/runs/${encodeURIComponent(runId)}/halt`,
      { method: 'POST' }
    );
    return body.data;
  },
};

export default photoScanApi;
